import axios from 'axios'

import {
  PlacementContent,
  PlacementContentArgs,
  PlacementContentType,
  QubitApiResponse,
  QubitTimeframes,
  QubitViewType,
} from '../../Types/Qubit'
import { ResolverProvider } from '../../types'

const mockQubitData = import('./QubitPlacements.mock').then(
  (mod) => mod.qubitPlacementsResponseMock,
)

const mockUrgencyMessagingQubitData = import('./QubitPlacements.mock').then(
  (mod) => mod.qubitPlacementsUrgencyMessagingMock,
)

const mockRecommendationRailData = import('./QubitPlacements.mock').then(
  (mod) => mod.qubitPlacementsRecommendationRailMock,
)

const schemaTimeframes = {
  [QubitTimeframes.LAST_HOUR]: 60,
  [QubitTimeframes.LAST_DAY]: 1440,
  [QubitTimeframes.LAST_2_DAYS]: 2880,
  [QubitTimeframes.LAST_3_DAYS]: 4320,
  [QubitTimeframes.LAST_WEEK]: 10080,
}

const transformForTally = (data) => {
  const timeframe = Object.keys(schemaTimeframes).find(
    (key) => key === data?.content?.config?.timeframe,
  )

  return {
    counter: data?.content?.config?.counter.toLowerCase(),
    op: data?.content?.config?.op,
    threshold: data?.content?.config?.threshold,
    timeframe: schemaTimeframes[timeframe || ''],
  }
}

const transformPlacementResponse = (
  placementContent: QubitApiResponse,
): PlacementContent => {
  if (!placementContent?.content) {
    return {
      ...placementContent,
      content: null,
    }
  }

  if ('content' in placementContent.content) {
    return {
      ...placementContent,
      content: {
        [PlacementContentType.BADGING]: {
          config: placementContent.content.config,
          imageUrl: placementContent.content.content.imageUrl,
          message: placementContent.content.content.message,
        },
      },
      callbackData: placementContent.callbackData,
      __typename: 'QubitPlacement',
    }
  }

  if ('recs' in placementContent.content) {
    return {
      ...placementContent,
      content: {
        [PlacementContentType.RECOMMENDATIONS]: {
          headline: placementContent.content.headline,
          recs: placementContent.content.recs,
        },
      },
      __typename: 'QubitPlacement',
    }
  }

  if ('title' in placementContent.content) {
    return {
      ...placementContent,
      content: {
        [PlacementContentType.PERSONALISED_CONTENT]: {
          title: placementContent.content.title,
          copy: placementContent.content.copy,
          horizontalImageSrc: placementContent.content.horizontalImageSrc,
          verticalImageSrc: placementContent.content.verticalImageSrc,
          cta: placementContent.content.cta,
          ctaText: placementContent.content.ctaText,
          style: placementContent.content.style,
        },
      },
    }
  }

  return {
    ...placementContent,
    content: {
      [PlacementContentType.BADGING]: {
        imageUrl: placementContent.content.imageUrl,
        message: placementContent.content.message,
      },
    },
    __typename: 'QubitPlacement',
  }
}

export const QubitPlacements: ResolverProvider = (
  _,
  { behaviours },
  config,
) => async (_, args: PlacementContentArgs) => {
  if (behaviours.override) {
    if (args.attributes.view?.type === QubitViewType.PRODUCT) {
      return mockUrgencyMessagingQubitData
    }
    if (args.attributes.view?.type === QubitViewType.CATEGORY) {
      return mockRecommendationRailData
    }
    return mockQubitData
  }

  if (!config.siteConfig.qubit) {
    return null
  }

  const graphqlQubitData = {
    operationName: 'PlacementContent',
    query: `query PlacementContent(
    $mode: Mode!
    $placementId: String!
    $attributes: Attributes!
    $previewOptions: PreviewOptions
    $resolveVisitorState: Boolean!
    $recsPreFilter: Boolean!
  ) {
    placementContent(
      mode: $mode
      placementId: $placementId
      attributes: $attributes
      previewOptions: $previewOptions
      resolveVisitorState: $resolveVisitorState
      recsPreFilter: $recsPreFilter
    ) {
      content
      callbackData
      visitorId
      __typename
    }
  }`,
    variables: args,
  }

  const qubitAPI = config.siteConfig.qubit.API

  try {
    const { data } = await axios.post<{
      data: { placementContent: QubitApiResponse }
    }>(qubitAPI, graphqlQubitData)

    if (!data) {
      return null
    }

    const {
      data: { placementContent },
    } = data
    const transformedResponse = transformPlacementResponse(placementContent)

    if (
      args.attributes.view?.type === QubitViewType.CATEGORY &&
      args.attributes.product?.id
    ) {
      const tally = transformForTally(placementContent)
      const tallyUrl = config.siteConfig.qubit.tallyAPI
        .replace('{siteCode}', `${config.siteConfig.qubit.trackingId}`)
        .replace('{counter}', `${tally.counter}`)
        .replace('{timeframe}', `${tally.timeframe}`)
        .replace('{productId}', `${args.attributes.product?.id}`)

      const { data: result } = await axios.get(tallyUrl)

      if (
        result.error ||
        (result.data !== '' &&
          transformedResponse.content &&
          transformedResponse.content[PlacementContentType.BADGING] &&
          Number(result.data) <
            Number(
              transformedResponse?.content[PlacementContentType.BADGING].config
                ?.threshold,
            ))
      ) {
        return null
      }

      return transformedResponse
    }

    return transformedResponse
  } catch (error) {
    console.warn(`Failed to fetch qubit data with error ${error.message}`)
    return null
  }
}
